import { useChatDeepLink } from "../hooks/useChatDeepLink"
import { CHAT_LINK_PARAM, entryPath, PLANNER_TAB } from "../lib/appEntryLink"
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react"
import { parse } from "@xmpp/jid"
import css from "./Root.module.css"
import utilsCss from "../components/utilsCss.module.css"
import ChatList from "../components/ChatList"
import { ChatInputTextAreaRef } from "../components/ChatInput"
import { XmppContext } from "../stream/xmppClient"
import ChatHistory, { ChatHistoryHandle } from "../components/ChatHistory"
import ChatHeader from "../components/ChatHeader"
import { Client } from "@xmpp/client"
import {
  Await,
  Navigate,
  useLoaderData,
  useSearchParams,
} from "react-router-dom"
import { useAppDispatch, useAppSelector, useAppStore } from "../reducers/hooks"
import {
  selectFirstDriverChatJid,
  selectIsDriverChatJid,
} from "@/hooks/useSortedChatsByLastEvent"
import { selectAccountChats } from "@/reducers/accountChatsSlice"
import AdHocCommands from "../api/adHocCommands"
import {
  changeActiveChat,
  dropPeekedChats,
  updateDraftMessage,
} from "../reducers/chatsSlice"
import { Message } from "../reducers/messagesSlice"
import {
  allUnreadCount,
  selectChats,
  roomsUnreadCount,
  selectActiveChat,
  selectChat,
  selectSlickshiftAIChat,
} from "../reducers/chatsSliceSelectors"
import MessageHelpers from "../lib/messageHelpers"
import {
  sendsTextAsAttachmentCaption,
  uploadAndSendMultipleFiles,
} from "../lib/uploadAndSendFiles"
import useIsTabActive from "../hooks/useIsTabActive"
import WebNotificationsPermissionRequest from "../components/WebNotificationsPermissionsRequest"
import {
  markMessageDisplayed,
  MessageOptions,
  sendMessage,
} from "../reducers/chatsSliceThunks"
import NotificationNewAppVersion from "../components/NotificationNewAppVersion"
import MobilePushNotificationsPermissionsRequest from "../components/MobilePushNotificationsPermissionsRequest"
import usePushNotifications from "../push_notifications/usePushNotifications"
import ChatListHeader from "../components/ChatListHeader"
import Loader from "../components/Loader"
import classNames from "classnames"
import ChatInfo from "../components/ChatInfo"
import { isIOS, isMobile } from "../utils/detectDevice"
import { DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import ProfileInfo from "../components/ProfileInfo"
import { useSelectChatNavigation } from "../hooks/useSelectChatNavigation"
import { shallowEqual } from "react-redux"
import { selectArchivedChats } from "@/reducers/chatsSettingsSelectors"
import {
  hideArchive,
  hideChatSearch,
  hideGlobalSearch,
  hideImageGallery,
  hideMobileMap,
  hidePDFViewer,
  isArchiveVisible,
  isChatSearchVisible,
  isGlobalSearchVisible,
  isImageGalleryVisible,
  isMobileMapVisible,
  isPDFViewerVisible,
} from "../reducers/overlaysSlice"
import { mobileRoutes, setMobileRoute, updateMobileRoute } from "./routes"
import {
  SendoutQueue,
  SendoutQueueItem,
  setQueue,
} from "../reducers/sendoutQueueSlice"
import posthog from "posthog-js"
import ChatHelpers from "../lib/chatHelpers"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import Archive from "@/components/Archive"
import { useFeatureFlagEnabled } from "posthog-js/react"
import UiLanguageSync from "@/lib/i18n/UiLanguageSync"
import { getCMRJid, getCarIssuesJid } from "@/jidUtils"
import { createPortal } from "react-dom"
import FleetOverview from "@/components/fleetOverview/FleetOverview"
import Sidebar from "@/components/sidebar/Sidebar"
import {
  changeActiveTab,
  selectTabs,
  TAB_TYPES,
  TabType,
  updateLastActiveChat,
} from "@/reducers/tabsSlice"
import PlanOrder from "@/components/analyzeOrderOnPage/PlanOrder"
import { DeliveryOrderReport } from "@/components/deliveryOrderReport/DeliveryOrderReport"
import { FuelReport } from "@/components/fuelReport/FuelReport"
import { DeliveryOrdersView } from "@/components/deliveryOrders/DeliveryOrdersView"
import DriverManagement from "@/components/driverManagement/DriverManagement"
import CrewBoard from "@/components/crewBoard/CrewBoard"
import DailyRoutes from "@/components/dailyRoutes/DailyRoutes"
import UserManagement from "@/components/userManagement/UserManagement"
import GpsManagement from "@/components/gpsManagement/GpsManagement"
import { AnalyzeOrderProvider } from "@/contexts/AnalyzeOrderContext"
import { APIProvider } from "@vis.gl/react-google-maps"
import { StatsPage } from "@/components/stats/StatsPage"
import { MyCars } from "@/components/myCars/MyCars"
import { selectUserChats } from "@/reducers/userChatsSlice"
import { findTranschatUserChat } from "@/lib/privateAgentUtils"
import WorkflowList from "@/components/slickAI/WorkflowList"
import WorkflowWidget, {
  AutomationsToggleButton,
} from "@/components/slickAI/WorkflowWidget"
import { TransportBriefDialog } from "@/components/ai-chat/TransportBriefDialog"
import MapWidget from "@/components/slickAI/MapWidget"
import { selectAiWidgetMapCount } from "@/reducers/aiWidgetMapSlice"
import { CmrRoomTabs } from "@/components/cmrReport/CmrRoomTabs"
import TimelineView from "@/components/timeline/TimelineView"
import PlannerTimelineView from "@/components/timeline/planner/PlannerTimelineView"
import { useStaleGpsWatcher } from "../lib/staleGpsWatcher"
import RoomSyncQueue from "@/lib/roomSyncQueue"
import { useAppBadge } from "@/hooks/useAppBadge"
import { Button } from "@/components/ui/button"
import { useTranslation } from "react-i18next"

const ChatDeepLinkLoading = ({ onCancel }: { onCancel: () => void }) => {
  const { t } = useTranslation()
  return (
    <div
      data-testid="chat-link-pending"
      role="status"
      className="flex h-full items-center justify-center gap-3 text-sm text-muted-foreground"
    >
      <Loader small />
      {t("app.root.openingLink")}
      <Button
        data-testid="cancel-chat-link"
        variant="outline"
        size="sm"
        onClick={onCancel}
      >
        {t("common.cancel")}
      </Button>
    </div>
  )
}

const NoChatsEmptyState = () => (
  <div className={utilsCss.center}>
    <Loader />
  </div>
)

const DocumentTitleBasedOnUnreadMessages = () => {
  const { myJid } = useContext(XmppContext)
  const allUnreadMessagesCount = useAppSelector(
    allUnreadCount(myJid.toString()),
  )
  const roomUnreadCount = useAppSelector(roomsUnreadCount(myJid.toString()))

  useEffect(() => {
    document.title =
      allUnreadMessagesCount > 0
        ? `(${allUnreadMessagesCount}/${roomUnreadCount}) Slickshift`
        : `Slickshift`
  }, [allUnreadMessagesCount, roomUnreadCount])

  // The badge counts rooms with unread messages, not the messages themselves.
  useAppBadge(allUnreadMessagesCount > 0 ? roomUnreadCount : 0)

  return null
}

// Remembers the last driver chat the dispatcher opened, across full app
// reloads (previousActiveChat is intentionally not persisted in redux). On the
// next load we re-select it if it's still an available driver chat.
const LAST_DRIVER_CHAT_KEY = "slickshift:lastDriverChatJid"
const readLastDriverChatJid = (): string | null => {
  try {
    return localStorage.getItem(LAST_DRIVER_CHAT_KEY)
  } catch {
    return null
  }
}
const writeLastDriverChatJid = (jid: string): void => {
  try {
    localStorage.setItem(LAST_DRIVER_CHAT_KEY, jid)
  } catch {
    // ignore (private mode / storage disabled)
  }
}

const SlickAiSidebar = ({
  chatJid,
  expanded,
  onExpandedChange,
}: {
  chatJid: string
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
}) => {
  const hasMaps = useAppSelector(selectAiWidgetMapCount(chatJid)) > 0
  return (
    <div className="flex h-full w-full flex-col">
      <WorkflowWidget expanded={expanded} onExpandedChange={onExpandedChange} />
      {hasMaps && (
        <div className="min-h-0 flex-1">
          <MapWidget chatJid={chatJid} />
        </div>
      )}
    </div>
  )
}

export const RootApp = () => {
  const { t } = useTranslation()
  const { client, myJid } = useContext(XmppContext)
  const store = useAppStore()
  const activeChat = useAppSelector(selectActiveChat, shallowEqual)
  // SlickAI automations widget collapse state. Collapsed by default; when
  // collapsed AND there's no map, the sidebar panel is dropped entirely so the
  // chat reclaims the width and a floating trigger is shown instead. When a map
  // is present the panel always stays (the map needs the width) and the widget
  // just collapses inline.
  const [aiAutomationsOpen, setAiAutomationsOpen] = React.useState(false)
  const aiSidebarHasMaps =
    useAppSelector(selectAiWidgetMapCount(activeChat?.jid ?? "")) > 0
  const carIssuesChat = useAppSelector(selectChat(getCarIssuesJid(myJid)))
  const dispatch = useAppDispatch()
  const chatHistoryRef = useRef<ChatHistoryHandle>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const chatListRef = useRef<HTMLDivElement>(null)
  const chatInputTextAreaRef = useRef<ChatInputTextAreaRef>(null)
  const isTabActive = useIsTabActive()
  const [infoOpened, setInfoOpened] = React.useState<
    "chat" | "profile" | undefined
  >()
  const [searchParams, setSearchParams] = useSearchParams()
  const hasChatDeepLink = searchParams.has(CHAT_LINK_PARAM)

  const { activeTab, previousActiveChat, previousTab } =
    useAppSelector(selectTabs)
  const chats = useAppSelector(selectChats)
  const accountChats = useAppSelector(selectAccountChats)
  const initialActiveTabRef = useRef(activeTab)
  const initialPreviousActiveChatRef = useRef(previousActiveChat)
  const didInitialSlickAITabRecoveryRef = useRef(false)
  const didInitialSpecialChatTabSyncRef = useRef(false)
  // Cross-reload restore of the last opened driver chat (localStorage). Until
  // this runs once, the write effect must not overwrite the stored jid.
  const didInitialDriverRestoreRef = useRef(false)

  // Handle tab query parameter from redirects
  useEffect(() => {
    const tabParam = searchParams.get("tab")
    if (tabParam === PLANNER_TAB) {
      dispatch(changeActiveTab({ activeTab: TAB_TYPES.PLAN_ORDER }))
      // Remove the query param after handling it
      searchParams.delete("tab")
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams, dispatch])

  useEffect(() => {
    // A crash or a hard reload can leave a peeked chat in persisted state;
    // it belongs to nobody, so it goes.
    dispatch(dropPeekedChats())
  }, [dispatch])

  const isPlanOrderEnabled = Boolean(useFeatureFlagEnabled("plan-order"))
  const areReportsEnabled = Boolean(useFeatureFlagEnabled("reports"))
  const isDeliveryOrdersEnabled = Boolean(
    useFeatureFlagEnabled("delivery-orders-ui"),
  )
  const isCarIssuesEnabled = Boolean(useFeatureFlagEnabled("car-issues"))
  const isStatsEnabled = Boolean(useFeatureFlagEnabled("stats"))
  const isTimelineViewEnabled = Boolean(useFeatureFlagEnabled("timeline-view"))
  const isPlannerTimelineViewEnabled = Boolean(
    useFeatureFlagEnabled("planner-timeline-view"),
  )
  const isFuelReportEnabled = Boolean(useFeatureFlagEnabled("fuel-report"))
  const isCrewBoardEnabled = Boolean(useFeatureFlagEnabled("crew-board"))
  const isDailyRoutesEnabled = Boolean(
    useFeatureFlagEnabled("delivery-manifest-route"),
  )
  const isMyCarsEnabled = Boolean(useFeatureFlagEnabled("my-cars"))

  const pdfViewerVisible = useAppSelector(isPDFViewerVisible)
  const imageGalleryVisible = useAppSelector(isImageGalleryVisible)
  const globalSearchVisible = useAppSelector(isGlobalSearchVisible)
  const chatSearchVisible = useAppSelector(isChatSearchVisible)
  const mobileMapVisible = useAppSelector(isMobileMapVisible)
  const archiveVisible = useAppSelector(isArchiveVisible)
  const archivedChats = useAppSelector(selectArchivedChats)
  const userChats = useAppSelector(selectUserChats)

  const slickshiftChatSelector = useMemo(
    () => selectSlickshiftAIChat(myJid),
    [myJid],
  )

  const onReturnToChatLists = useCallback(
    (
      usingBackButton: boolean = false,
      options: { forceBackToChats: boolean } = { forceBackToChats: false },
    ) => {
      if (isMobile) {
        if (options.forceBackToChats) {
          chatContainerRef.current?.classList.remove(css.active)
          chatListRef.current?.classList.remove(css.hidden)
          setMobileRoute(mobileRoutes.CHAT_LIST)
          return
        }

        if (pdfViewerVisible) {
          dispatch(hidePDFViewer())
          updateMobileRoute(mobileRoutes.CHAT)
        } else if (imageGalleryVisible) {
          dispatch(hideImageGallery())
          updateMobileRoute(mobileRoutes.CHAT)
        } else if (globalSearchVisible) {
          dispatch(hideGlobalSearch())
          updateMobileRoute(mobileRoutes.CHAT_LIST)
        } else if (chatSearchVisible) {
          dispatch(hideChatSearch())
          updateMobileRoute(mobileRoutes.CHAT)
        } else if (mobileMapVisible) {
          dispatch(hideMobileMap())
          updateMobileRoute(mobileRoutes.CHAT)
        } else if (archiveVisible) {
          if (window.location.hash === mobileRoutes.ARCHIVE) {
            chatContainerRef.current?.classList.remove(css.active)
            chatListRef.current?.classList.remove(css.hidden)
          } else if (window.location.hash === mobileRoutes.CHAT_LIST) {
            dispatch(hideArchive())
            updateMobileRoute(mobileRoutes.CHAT_LIST)
          }
        } else {
          if (isIOS && usingBackButton) {
            chatContainerRef.current?.classList.remove(css.withTransition)
            chatListRef.current?.classList.remove(css.withTransition)
          }
          chatContainerRef.current?.classList.remove(css.active)
          chatListRef.current?.classList.remove(css.hidden)
          updateMobileRoute(mobileRoutes.CHAT_LIST)
          if (isIOS && usingBackButton) {
            setTimeout(() => {
              chatContainerRef.current?.classList.add(css.withTransition)
              chatListRef.current?.classList.add(css.withTransition)
            }, 350)
          }
        }
      } else {
        chatContainerRef.current?.classList.remove(css.active)
        chatListRef.current?.classList.remove(css.hidden)
      }
    },
    [
      pdfViewerVisible,
      imageGalleryVisible,
      globalSearchVisible,
      chatSearchVisible,
      mobileMapVisible,
      archiveVisible,
      dispatch,
    ],
  )

  const fleetOverviewEnabled = Boolean(useFeatureFlagEnabled("fleet-overview"))

  const fleetOverviewOpen = activeTab === TAB_TYPES.FLEET_OVERVIEW

  useLayoutEffect(() => {
    if (activeChat && !activeChat.active) {
      dispatch(changeActiveChat({ activeChatJid: activeChat.jid }))
    }
  }, [activeChat, dispatch])

  const { navigateToChat } = useSelectChatNavigation(onReturnToChatLists)

  const dispatchDraftAndActiveChatChange = useCallback(
    (chatJid: string) => {
      if (window.isRouteUpdateTrackingCountdownActive) {
        console.log("Saving reference route")
        const driver = ChatHelpers.findDriverParticipant(activeChat)
        if (driver) {
          AdHocCommands.upsertReferenceRoute(window.XMPP!, driver?.jid)
        }
        window.isRouteUpdateTrackingCountdownActive = false
      }
      // Save draft message if needed
      if (
        chatInputTextAreaRef.current &&
        activeChat &&
        activeChat.draftMessage !== chatInputTextAreaRef.current.value()
      ) {
        dispatch(
          updateDraftMessage({
            chatJid: activeChat.jid,
            draftMessage: chatInputTextAreaRef.current.value(),
          }),
        )
      }

      // Essential operations for chat change
      navigateToChat()
      dispatch(changeActiveChat({ activeChatJid: chatJid }))

      // Defer non-essential operations
      setTimeout(() => {
        window.analytics.track("ChatOpen", { chatJid })
      }, 0)

      // Focus input after state update is complete
      if (!isMobile) {
        requestAnimationFrame(() => {
          chatInputTextAreaRef.current?.focus()
        })
      }
    },
    [navigateToChat, dispatch, activeChat],
  )

  const slickshiftAIChat = useAppSelector(slickshiftChatSelector)
  const ownTranschatJid = useMemo(
    () => findTranschatUserChat(userChats, myJid.local)?.jid,
    [userChats, myJid.local],
  )

  const isPrivateAgentChatJid = useCallback(
    (chatJid?: string) =>
      Boolean(
        chatJid &&
          (chatJid === slickshiftAIChat?.jid || chatJid === ownTranschatJid),
      ),
    [slickshiftAIChat?.jid, ownTranschatJid],
  )

  const cmrJid = useMemo(() => getCMRJid(myJid), [myJid])
  const carIssuesJid = useMemo(() => getCarIssuesJid(myJid), [myJid])

  const isSpecialChatJid = useCallback(
    (chatJid?: string) =>
      Boolean(
        chatJid &&
          (chatJid === cmrJid ||
            chatJid === carIssuesJid ||
            isPrivateAgentChatJid(chatJid)),
      ),
    [carIssuesJid, cmrJid, isPrivateAgentChatJid],
  )

  const regularRoomJids = useMemo(
    () =>
      chats
        .filter(
          (chat) =>
            chat.room &&
            !archivedChats.includes(chat.jid) &&
            !isSpecialChatJid(chat.jid),
        )
        .map((chat) => chat.jid),
    [archivedChats, chats, isSpecialChatJid],
  )

  const chatTabTargetJid = useMemo(() => {
    if (
      previousActiveChat &&
      !isSpecialChatJid(previousActiveChat) &&
      regularRoomJids.includes(previousActiveChat)
    ) {
      return previousActiveChat
    }

    return regularRoomJids[0]
  }, [isSpecialChatJid, previousActiveChat, regularRoomJids])

  useEffect(() => {
    if (hasChatDeepLink) return
    if (didInitialSlickAITabRecoveryRef.current) return

    if (initialActiveTabRef.current !== TAB_TYPES.SLICK_AI) {
      didInitialSlickAITabRecoveryRef.current = true
      return
    }

    dispatch(changeActiveTab({ activeTab: TAB_TYPES.CHATS }))

    const previousChatJid = initialPreviousActiveChatRef.current
    const isAgentChatActive = isPrivateAgentChatJid(activeChat?.jid)

    if (previousChatJid && isAgentChatActive) {
      dispatch(changeActiveChat({ activeChatJid: previousChatJid }))
    }

    didInitialSlickAITabRecoveryRef.current = true
  }, [activeChat?.jid, dispatch, hasChatDeepLink, isPrivateAgentChatJid])

  // When no chat was flagged active, selectActiveChat falls back to chats[0],
  // which is often the CMR / Car Issues room (they're unshifted to the top).
  // If a regular room is available, keep users in Chats and let the Chats-tab
  // fallback below pick it. Otherwise, align the tab with the special room.
  useEffect(() => {
    if (hasChatDeepLink) return
    if (didInitialSpecialChatTabSyncRef.current) return
    if (!activeChat?.jid) return

    didInitialSpecialChatTabSyncRef.current = true

    if (initialActiveTabRef.current !== TAB_TYPES.CHATS) return
    if (chatTabTargetJid) return

    if (activeChat.jid === cmrJid) {
      dispatch(changeActiveTab({ activeTab: TAB_TYPES.CMR }))
    } else if (activeChat.jid === carIssuesJid) {
      dispatch(changeActiveTab({ activeTab: TAB_TYPES.CAR_ISSUES }))
    }
  }, [
    activeChat?.jid,
    carIssuesJid,
    chatTabTargetJid,
    cmrJid,
    dispatch,
    hasChatDeepLink,
  ])

  useEffect(() => {
    if (hasChatDeepLink) return
    if (activeTab !== TAB_TYPES.CHATS) return
    if (!activeChat?.jid || !isSpecialChatJid(activeChat.jid)) return

    const hasValidPrevious = Boolean(
      previousActiveChat &&
        !isSpecialChatJid(previousActiveChat) &&
        regularRoomJids.includes(previousActiveChat),
    )
    // With a remembered chat (same session), restore it. Otherwise select the
    // first driver chat, falling back to the first regular room. (Cross-reload
    // restore of the last driver chat is handled by the initial-restore effect
    // below, which reads localStorage once data is ready.)
    const target = hasValidPrevious
      ? previousActiveChat
      : (selectFirstDriverChatJid(store.getState()) ?? chatTabTargetJid)

    if (!target || target === activeChat.jid) return

    dispatch(changeActiveChat({ activeChatJid: target }))
    if (previousActiveChat !== target) {
      dispatch(updateLastActiveChat({ previousActiveChat: target }))
    }
  }, [
    activeChat?.jid,
    activeTab,
    chatTabTargetJid,
    dispatch,
    isSpecialChatJid,
    hasChatDeepLink,
    previousActiveChat,
    regularRoomJids,
    store,
  ])

  // On a fresh load, once driver chats are available, restore the last opened
  // driver chat (localStorage) — overriding the persisted `active` chat — or
  // fall back to the first driver. Runs exactly once.
  useEffect(() => {
    if (hasChatDeepLink) return
    if (didInitialDriverRestoreRef.current) return
    const state = store.getState()
    const firstDriver = selectFirstDriverChatJid(state)
    if (!firstDriver) return // chats/accountChats not ready yet — wait
    didInitialDriverRestoreRef.current = true

    if (initialActiveTabRef.current !== TAB_TYPES.CHATS) return

    const stored = readLastDriverChatJid()
    const target =
      stored && selectIsDriverChatJid(state, stored) ? stored : firstDriver
    writeLastDriverChatJid(target)

    const currentActive = state.chats.find((c) => c.room && c.active)?.jid
    if (target !== currentActive) {
      dispatch(changeActiveChat({ activeChatJid: target }))
    }
  }, [chats, accountChats, store, dispatch, hasChatDeepLink])

  // Remember the last opened driver chat (localStorage) for the next reload.
  // Guarded so we don't clobber the stored jid before the initial restore has
  // read it (chats/accountChats load asynchronously on reload).
  useEffect(() => {
    if (!didInitialDriverRestoreRef.current) return
    const jid = activeChat?.jid
    if (jid && selectIsDriverChatJid(store.getState(), jid)) {
      writeLastDriverChatJid(jid)
    }
  }, [activeChat?.jid, store])

  useEffect(() => {
    if (hasChatDeepLink) return
    if (activeTab !== TAB_TYPES.CHATS || previousTab !== TAB_TYPES.TIMELINE) {
      return
    }

    if (!previousActiveChat || !isPrivateAgentChatJid(activeChat?.jid)) {
      return
    }

    dispatch(changeActiveChat({ activeChatJid: previousActiveChat }))
  }, [
    activeChat?.jid,
    activeTab,
    dispatch,
    isPrivateAgentChatJid,
    previousActiveChat,
    previousTab,
    hasChatDeepLink,
  ])

  const openChat = useCallback(
    (chatJid: string, tab?: TabType) => {
      // Start performance measurement
      performance.mark("ChatChange", { detail: { jid: chatJid } })
      const isAgentChatTarget = isPrivateAgentChatJid(chatJid)

      if (
        chatJid &&
        !isAgentChatTarget &&
        chatJid !== cmrJid &&
        chatJid !== carIssuesJid
      ) {
        dispatch(updateLastActiveChat({ previousActiveChat: chatJid }))
      } else if (
        isAgentChatTarget &&
        activeChat?.jid &&
        activeChat.jid !== chatJid &&
        activeChat.jid !== cmrJid &&
        activeChat.jid !== carIssuesJid &&
        activeChat.jid !== slickshiftAIChat?.jid &&
        activeChat.jid !== ownTranschatJid
      ) {
        dispatch(updateLastActiveChat({ previousActiveChat: activeChat.jid }))
      }
      if (tab) {
        dispatch(changeActiveTab({ activeTab: tab }))
      }
      // Essential operation: update state
      dispatchDraftAndActiveChatChange(chatJid)
      RoomSyncQueue.prioritize(chatJid)

      chatContainerRef.current?.classList.add(css.active)
      chatListRef.current?.classList.add(css.hidden)
    },
    [
      activeChat,
      dispatch,
      dispatchDraftAndActiveChatChange,
      isPrivateAgentChatJid,
      carIssuesJid,
      cmrJid,
      ownTranschatJid,
      slickshiftAIChat?.jid,
    ],
  )

  const tabForChat = useCallback(
    (jid: string): TabType =>
      jid === cmrJid
        ? TAB_TYPES.CMR
        : jid === carIssuesJid
          ? TAB_TYPES.CAR_ISSUES
          : isPrivateAgentChatJid(jid)
            ? TAB_TYPES.SLICK_AI
            : TAB_TYPES.CHATS,
    [carIssuesJid, cmrJid, isPrivateAgentChatJid],
  )

  const openLinkedChat = useCallback(
    (jid: string) => {
      didInitialSlickAITabRecoveryRef.current = true
      didInitialSpecialChatTabSyncRef.current = true
      didInitialDriverRestoreRef.current = true
      dispatch(hideArchive())
      openChat(jid, tabForChat(jid))
    },
    [dispatch, openChat, tabForChat],
  )
  const { isPending: isChatLinkPending, cancel: cancelChatDeepLink } =
    useChatDeepLink(openLinkedChat)

  const cancelPendingChatForUserNavigation = useCallback(() => {
    if (isChatLinkPending) {
      didInitialSlickAITabRecoveryRef.current = true
      didInitialSpecialChatTabSyncRef.current = true
      didInitialDriverRestoreRef.current = true
    }
    cancelChatDeepLink()
  }, [cancelChatDeepLink, isChatLinkPending])

  const handleOnActiveChatChange = useCallback(
    (jid: string, tab?: TabType) => {
      openChat(jid, tab ?? (isChatLinkPending ? tabForChat(jid) : undefined))
      cancelPendingChatForUserNavigation()
    },
    [
      cancelPendingChatForUserNavigation,
      isChatLinkPending,
      openChat,
      tabForChat,
    ],
  )

  const handleOnActiveChatChangeFromNotification = useCallback(
    (chatJid: string) => {
      cancelPendingChatForUserNavigation()
      setTimeout(() => {
        requestAnimationFrame(() => {
          onReturnToChatLists(false, { forceBackToChats: true })
          dispatchDraftAndActiveChatChange(chatJid)
        })
      }, 400)

      setTimeout(() => {
        requestAnimationFrame(() => {
          chatContainerRef.current?.classList.add(css.active)
          chatListRef.current?.classList.add(css.hidden)
        })
      }, 600)
    },
    [
      cancelPendingChatForUserNavigation,
      dispatchDraftAndActiveChatChange,
      onReturnToChatLists,
    ],
  )

  const handleOnSetSendoutQueue = useCallback(
    (sendoutQueue: string) => {
      if (posthog.isFeatureEnabled("mobile-sendout-worker")) {
        const parsedQueue = JSON.parse(sendoutQueue) as Array<
          SendoutQueueItem & { attempts?: number }
        >
        const queueWithoutAttempts: SendoutQueue = parsedQueue.map((item) => {
          const queueItem = { ...item }
          delete queueItem.attempts
          return queueItem
        })
        dispatch(setQueue({ queue: queueWithoutAttempts }))
      }
    },
    [dispatch],
  )

  const onPushNotificationClick = useCallback(
    (toJid: string) => {
      if (!isIOS) return
      handleOnActiveChatChangeFromNotification(toJid)
    },
    [handleOnActiveChatChangeFromNotification],
  )

  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.triggerChatChange =
      handleOnActiveChatChangeFromNotification
  }
  window.triggerChatChange = handleOnActiveChatChangeFromNotification
  window.setSendoutQueue = handleOnSetSendoutQueue

  usePushNotifications(onPushNotificationClick)

  const markMessageDisplayedIfNeeded = useCallback(
    (message: Message) => {
      if (!isTabActive) return

      if (
        !MessageHelpers.isToMyself(message) &&
        !MessageHelpers.isDisplayed(message, [myJid.local]) &&
        MessageHelpers.isIncoming(message) &&
        (message.type === "chat" || message.type === "groupchat")
      ) {
        dispatch(markMessageDisplayed(client, message))
      }
    },
    [client, dispatch, isTabActive, myJid],
  )

  const handleOnSend = async (
    text: string,
    files: FileList,
    options: MessageOptions = {},
  ) => {
    if (activeChat.draftMessage) {
      dispatch(
        updateDraftMessage({ chatJid: activeChat.jid, draftMessage: "" }),
      )
    }

    // In the agent chat the text rides on the first attachment as its
    // caption, so a pasted screenshot and its question arrive as one message.
    const caption = sendsTextAsAttachmentCaption(activeChat.jid, text, files)
      ? text
      : undefined

    if (!caption && !text.match(/^\s*$/)) {
      // only send non-blank messages
      if (chatHistoryRef.current?.shifting) {
        chatHistoryRef.current.shifting.current = false
      }
      chatHistoryRef.current?.setStickToTheBottom(true)
      // Awaited on purpose: with translation on, the optimistic local echo
      // only happens once the translation comes back, so resolving early
      // left the composer cleared and the history empty for as long as the
      // translate lambda took — up to ~10s on a cold container, plenty of
      // time for the dispatcher to retype and send twice. Awaiting is also
      // what keeps ChatInput's controls locked for the whole send, which is
      // how overlapping sends to one chat are prevented. It keeps the
      // message ahead of any attachments uploaded below, too.
      await dispatch(
        sendMessage(text, client, myJid.toString(), activeChat, options),
      )
    }

    await uploadAndSendMultipleFiles(files, { client, dispatch }, { caption })
  }

  const handleOnToggleChatInfo = useCallback(
    (value: "chat" | "profile") => () => {
      return setInfoOpened(infoOpened === value ? undefined : value)
    },
    [infoOpened],
  )

  const displayedTab = isChatLinkPending ? TAB_TYPES.CHATS : activeTab

  if (isMobile) {
    return (
      <div className={css.mainContainer}>
        {!activeChat && (
          <>
            <NoChatsEmptyState />
          </>
        )}
        {activeChat && (
          <>
            <NotificationNewAppVersion />
            <DocumentTitleBasedOnUnreadMessages />
            <MobilePushNotificationsPermissionsRequest />
            <div
              className={classNames(css.chatListContainer, css.withTransition)}
              ref={chatListRef}
            >
              <ChatListHeader
                onOpenChatInfo={handleOnToggleChatInfo("profile")}
                onActiveChatChange={handleOnActiveChatChange}
              />
              <div className="relative h-full overflow-hidden">
                <div
                  className={`${css.chatList} absolute inset-0 w-full transition-transform duration-300 ease-in-out`}
                  style={{
                    transform: archiveVisible
                      ? "translateX(-15%)"
                      : "translateX(0)",
                  }}
                >
                  <ChatList onActiveChatChange={handleOnActiveChatChange} />
                </div>
                <div
                  className={`${css.chatList} absolute inset-0 w-full transition-transform duration-300 ease-in-out`}
                  style={{
                    transform: archiveVisible
                      ? "translateX(0)"
                      : "translateX(100%)",
                  }}
                >
                  <Archive onActiveChatChange={handleOnActiveChatChange} />
                </div>
              </div>
            </div>
            <div
              className={classNames(css.chatContainer, css.withTransition, {
                [css.small]: infoOpened,
              })}
              ref={chatContainerRef}
            >
              <DndProvider backend={HTML5Backend}>
                {isChatLinkPending ? (
                  <ChatDeepLinkLoading
                    onCancel={cancelPendingChatForUserNavigation}
                  />
                ) : (
                  <ChatHistory
                    onReturnToChatLists={onReturnToChatLists}
                    onActiveChatChange={handleOnActiveChatChange}
                    onOpenChatInfo={handleOnToggleChatInfo("chat")}
                    onSend={handleOnSend}
                    key={activeChat.jid}
                    chat={activeChat}
                    chatHistoryRef={chatHistoryRef}
                    chatInputRef={chatInputTextAreaRef}
                    onFileDropped={(files: FileList) => handleOnSend("", files)}
                    markMessageDisplayed={markMessageDisplayedIfNeeded}
                  />
                )}
              </DndProvider>
            </div>
            <ChatInfo
              key={activeChat.jid}
              chat={activeChat}
              isOpen={infoOpened === "chat"}
              onClose={handleOnToggleChatInfo("chat")}
            />
            <ProfileInfo
              isOpen={infoOpened === "profile"}
              onClose={handleOnToggleChatInfo("profile")}
            />
          </>
        )}
      </div>
    )
  }

  return (
    <div className={css.mainContainer}>
      {!activeChat && (
        <>
          <NoChatsEmptyState />
        </>
      )}
      {activeChat && (
        <>
          <NotificationNewAppVersion />
          <DocumentTitleBasedOnUnreadMessages />
          {!isMobile && <WebNotificationsPermissionRequest />}
          {isMobile && <MobilePushNotificationsPermissionsRequest />}
          <DndProvider backend={HTML5Backend}>
            <Sidebar
              onActiveChatChange={handleOnActiveChatChange}
              onNavigate={cancelPendingChatForUserNavigation}
            />
            {displayedTab === TAB_TYPES.PLAN_ORDER && isPlanOrderEnabled && (
              <APIProvider apiKey={import.meta.env.REACT_APP_GOOGLE_MAP_KEY}>
                <PlanOrder />
              </APIProvider>
            )}
            {displayedTab === TAB_TYPES.REPORTS && areReportsEnabled && (
              <DeliveryOrderReport
                onNavigateToChat={handleOnActiveChatChange}
              />
            )}
            {displayedTab === TAB_TYPES.FUEL_REPORT && isFuelReportEnabled && (
              <FuelReport onNavigateToChat={handleOnActiveChatChange} />
            )}
            {displayedTab === TAB_TYPES.DELIVERY_ORDERS &&
              isDeliveryOrdersEnabled && <DeliveryOrdersView client={client} />}
            {displayedTab === TAB_TYPES.DRIVER_MANAGEMENT && (
              <DriverManagement />
            )}
            {displayedTab === TAB_TYPES.CREW_BOARD && isCrewBoardEnabled && (
              <CrewBoard />
            )}
            {displayedTab === TAB_TYPES.DAILY_ROUTES &&
              isDailyRoutesEnabled && <DailyRoutes />}
            {displayedTab === TAB_TYPES.USER_MANAGEMENT && <UserManagement />}
            {displayedTab === TAB_TYPES.GPS_MANAGEMENT && <GpsManagement />}
            {displayedTab === TAB_TYPES.STATS && isStatsEnabled && (
              <StatsPage />
            )}
            {displayedTab === TAB_TYPES.MY_CARS && isMyCarsEnabled && (
              <MyCars />
            )}
            {displayedTab === TAB_TYPES.CHATS && (
              <ResizablePanelGroup
                autoSaveId="slickshift-chat-layout"
                direction="horizontal"
                className="h-full"
              >
                <ResizablePanel defaultSize={20} minSize={15}>
                  <div className="flex">
                    <div
                      className={classNames(
                        css.chatListContainer,
                        css.withTransition,
                        "bg-sidebar",
                      )}
                      ref={chatListRef}
                    >
                      <ChatListHeader
                        onOpenChatInfo={handleOnToggleChatInfo("profile")}
                        onActiveChatChange={handleOnActiveChatChange}
                      />
                      <div
                        className="relative overflow-hidden"
                        style={{ height: "calc(100vh - 65px)" }}
                      >
                        <div
                          className={`${css.chatList} absolute inset-0 w-full transition-transform duration-300 ease-in-out`}
                          style={{
                            transform: archiveVisible
                              ? "translateX(-15%)"
                              : "translateX(0)",
                          }}
                        >
                          <ChatList
                            onActiveChatChange={handleOnActiveChatChange}
                          />
                        </div>
                        <div
                          className={`${css.chatList} absolute inset-0 w-full transition-transform duration-300 ease-in-out`}
                          style={{
                            transform: archiveVisible
                              ? "translateX(0)"
                              : "translateX(100%)",
                          }}
                        >
                          <Archive
                            onActiveChatChange={handleOnActiveChatChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </ResizablePanel>
                <ResizableHandle withHandle />
                <ResizablePanel defaultSize={80} minSize={50}>
                  <div
                    className={classNames(
                      css.chatContainer,
                      css.withTransition,
                      {
                        [css.small]: infoOpened,
                      },
                    )}
                    ref={chatContainerRef}
                  >
                    <AnalyzeOrderProvider>
                      {isChatLinkPending ? (
                        <ChatDeepLinkLoading
                          onCancel={cancelPendingChatForUserNavigation}
                        />
                      ) : (
                        <ChatHistory
                          onReturnToChatLists={onReturnToChatLists}
                          onOpenChatInfo={handleOnToggleChatInfo("chat")}
                          onSend={handleOnSend}
                          chat={activeChat}
                          chatHistoryRef={chatHistoryRef}
                          chatInputRef={chatInputTextAreaRef}
                          onFileDropped={(files: FileList) =>
                            handleOnSend("", files)
                          }
                          markMessageDisplayed={markMessageDisplayedIfNeeded}
                          onActiveChatChange={handleOnActiveChatChange}
                        />
                      )}
                    </AnalyzeOrderProvider>
                  </div>
                </ResizablePanel>
              </ResizablePanelGroup>
            )}
            {displayedTab === TAB_TYPES.SLICK_AI && (
              <div className="relative flex h-full w-full flex-col">
                <ChatHeader
                  chat={activeChat}
                  onReturnToChatLists={onReturnToChatLists}
                  onOpenChatInfo={handleOnToggleChatInfo("chat")}
                  onActiveChatChange={handleOnActiveChatChange}
                  titleOverride="Slick AI"
                  hideActions
                />
                <ResizablePanelGroup
                  // Remount when the sidebar panel appears/disappears so the
                  // panel layout re-applies cleanly (avoids a stale 2-panel
                  // saved layout when there's only the chat panel).
                  key={
                    aiAutomationsOpen || aiSidebarHasMaps
                      ? "slickai-two"
                      : "slickai-one"
                  }
                  autoSaveId="slickshift-slickai-layout-v2"
                  direction="horizontal"
                  className="min-h-0 flex-1"
                >
                  <ResizablePanel defaultSize={76} minSize={40}>
                    <div
                      className={classNames(
                        css.chatContainer,
                        css.withTransition,
                        {
                          [css.small]: infoOpened,
                        },
                      )}
                      ref={chatContainerRef}
                    >
                      <AnalyzeOrderProvider>
                        <ChatHistory
                          showHeader={false}
                          onReturnToChatLists={onReturnToChatLists}
                          onOpenChatInfo={handleOnToggleChatInfo("chat")}
                          onSend={handleOnSend}
                          chat={activeChat}
                          chatHistoryRef={chatHistoryRef}
                          chatInputRef={chatInputTextAreaRef}
                          onFileDropped={(files: FileList) =>
                            handleOnSend("", files)
                          }
                          markMessageDisplayed={markMessageDisplayedIfNeeded}
                          onActiveChatChange={handleOnActiveChatChange}
                        />
                      </AnalyzeOrderProvider>
                    </div>
                  </ResizablePanel>
                  {(aiAutomationsOpen || aiSidebarHasMaps) && (
                    <>
                      <ResizableHandle className="bg-transparent transition-colors hover:bg-border data-[resize-handle-state=drag]:bg-border data-[resize-handle-state=hover]:bg-border" />
                      <ResizablePanel defaultSize={24} minSize={15}>
                        <SlickAiSidebar
                          chatJid={activeChat.jid}
                          expanded={aiAutomationsOpen}
                          onExpandedChange={setAiAutomationsOpen}
                        />
                      </ResizablePanel>
                    </>
                  )}
                </ResizablePanelGroup>
                {!(aiAutomationsOpen || aiSidebarHasMaps) && (
                  <div className="absolute right-4 top-3 z-20">
                    <AutomationsToggleButton
                      onClick={() => setAiAutomationsOpen(true)}
                    />
                  </div>
                )}
              </div>
            )}
            {displayedTab === TAB_TYPES.TIMELINE &&
              isPlannerTimelineViewEnabled && <PlannerTimelineView />}
            {displayedTab === TAB_TYPES.TIMELINE &&
              !isPlannerTimelineViewEnabled &&
              isTimelineViewEnabled && (
                <TimelineView
                  workflowContent={<WorkflowList showHeader={false} />}
                  chatContent={
                    <div
                      className={classNames(
                        css.chatContainer,
                        css.withTransition,
                        "min-h-0",
                        {
                          [css.small]: infoOpened,
                        },
                      )}
                      ref={chatContainerRef}
                    >
                      <AnalyzeOrderProvider>
                        <ChatHistory
                          onReturnToChatLists={onReturnToChatLists}
                          onOpenChatInfo={handleOnToggleChatInfo("chat")}
                          onSend={handleOnSend}
                          chat={activeChat}
                          showHeader={false}
                          chatHistoryRef={chatHistoryRef}
                          chatInputRef={chatInputTextAreaRef}
                          onFileDropped={(files: FileList) =>
                            handleOnSend("", files)
                          }
                          markMessageDisplayed={markMessageDisplayedIfNeeded}
                          onActiveChatChange={handleOnActiveChatChange}
                        />
                      </AnalyzeOrderProvider>
                    </div>
                  }
                />
              )}
            {displayedTab === TAB_TYPES.CMR && (
              <div
                className={classNames(css.chatContainer, css.withTransition, {
                  [css.small]: infoOpened,
                  "absolute inset-0 left-[48px] bg-[var(--background-primary)]":
                    !imageGalleryVisible && !pdfViewerVisible,
                })}
                ref={chatContainerRef}
              >
                <CmrRoomTabs
                  chatContent={
                    <AnalyzeOrderProvider>
                      <ChatHistory
                        onReturnToChatLists={onReturnToChatLists}
                        onOpenChatInfo={handleOnToggleChatInfo("chat")}
                        onSend={handleOnSend}
                        chat={activeChat}
                        chatHistoryRef={chatHistoryRef}
                        chatInputRef={chatInputTextAreaRef}
                        onFileDropped={(files: FileList) =>
                          handleOnSend("", files)
                        }
                        markMessageDisplayed={markMessageDisplayedIfNeeded}
                        onActiveChatChange={handleOnActiveChatChange}
                      />
                    </AnalyzeOrderProvider>
                  }
                  onNavigateToChat={handleOnActiveChatChange}
                />
              </div>
            )}
            {displayedTab === TAB_TYPES.CAR_ISSUES && isCarIssuesEnabled && (
              <div
                className={classNames(css.chatContainer, css.withTransition, {
                  [css.small]: infoOpened,
                  "absolute inset-0 left-[48px] bg-[var(--background-primary)]":
                    !imageGalleryVisible && !pdfViewerVisible,
                })}
                ref={chatContainerRef}
              >
                {carIssuesChat ? (
                  <ChatHistory
                    onReturnToChatLists={onReturnToChatLists}
                    onOpenChatInfo={handleOnToggleChatInfo("chat")}
                    onSend={() => {}}
                    chat={carIssuesChat}
                    chatHistoryRef={chatHistoryRef}
                    chatInputRef={chatInputTextAreaRef}
                    onFileDropped={() => {}}
                    markMessageDisplayed={markMessageDisplayedIfNeeded}
                    onActiveChatChange={handleOnActiveChatChange}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    {t("app.root.carIssuesUnavailable")}
                  </div>
                )}
              </div>
            )}
          </DndProvider>
          <ChatInfo
            key={activeChat.jid}
            chat={activeChat}
            isOpen={infoOpened === "chat"}
            onClose={handleOnToggleChatInfo("chat")}
          />
          <ProfileInfo
            isOpen={infoOpened === "profile"}
            onClose={handleOnToggleChatInfo("profile")}
          />
          {fleetOverviewOpen &&
            fleetOverviewEnabled &&
            createPortal(
              <FleetOverview
                onClose={() => {}}
                onNavigateToChat={handleOnActiveChatChange}
              />,
              document.body,
            )}
        </>
      )}
    </div>
  )
}

const WithMyJid = ({ client, myJid }: { client: Client; myJid: string }) => {
  const providerValue = useMemo(
    () => ({ client, myJid: parse(myJid) }),
    [client, myJid],
  )

  return (
    <XmppContext.Provider value={providerValue}>
      <RootApp />
      <TransportBriefDialog />
    </XmppContext.Provider>
  )
}

const Root = () => {
  const data = useLoaderData() as { myJid: string }

  useStaleGpsWatcher()

  useEffect(() => {
    if (isMobile) {
      setMobileRoute(mobileRoutes.CHAT_LIST)
    }
  }, [])
  return (
    <>
      <UiLanguageSync />
      <React.Suspense fallback={<Loader className={utilsCss.center} />}>
        <Await resolve={data.myJid}>
          {(myJid) => {
            if (!myJid || typeof myJid !== "string") {
              window.analytics.track("MyJidMissing", {
                myJidType: typeof myJid,
                myJidValue:
                  myJid === undefined
                    ? "undefined"
                    : myJid === null
                      ? "null"
                      : "other",
                hadXmpp: !!window.XMPP,
                visibilityState: document.visibilityState,
              })
              return (
                <Navigate
                  to={entryPath("/signin", window.location.search)}
                  replace
                />
              )
            }
            return <WithMyJid client={window.XMPP!} myJid={myJid} />
          }}
        </Await>
      </React.Suspense>
    </>
  )
}

export default Root
